import { GameConfig } from "@/config/GameConfig";
import type { HeadlineKind } from "./mapEvents";

/**
 * PR31 follow-up (semantic CHAOS result colors). A CHAOS pick itself
 * always reads as CHAOS's own purple identity ("CHAOS!", `neutral` here) -
 * but once the outcome resolves, the result headline's color now
 * communicates WHAT KIND of thing just happened, on top of (never
 * instead of) its explicit text: a player who only glances at the color
 * still gets "good/bad/risky/strange", while the text itself stays the
 * actual source of truth (color is never the only distinction - see
 * `headlineSemanticColor()`'s doc).
 *
 * Every category reuses an EXISTING `GameConfig.colors` value already
 * established elsewhere in this game's palette for a closely related
 * meaning - nothing here is a new hue:
 *  - `positive` reuses JACKPOT's own existing gold (`star`) - already the
 *    color of JACKPOT's burst ring/body pulse/frame flash everywhere else
 *    in `VisualFeedbackController`, so its headline now matches the rest
 *    of its own presentation rather than contradicting it with green.
 *  - `negative` reuses the existing generic danger red (`danger`).
 *  - `risk` reuses BLACK HOLE's existing amber/orange ring color
 *    (`blackHoleRing`) - already this game's one "hazard warmth" accent,
 *    distinct from JACKPOT's gold and RUSH's own gold (`biteRush`).
 *  - `spatial` reuses WORMHOLE's own existing cyan (`wormholeCyan`).
 *  - `relief` reuses SPEED TRIAL's existing dedicated blue
 *    (`speedTrialBlue`) - cooler and calmer than `wormholeCyan`, reserved
 *    for a future "slow/relief" CHAOS result (e.g. SLOW MOTION, which
 *    does not exist yet) so it never has to invent its own color when it
 *    does.
 */
export type SemanticFeedbackCategory = "neutral" | "positive" | "negative" | "risk" | "spatial" | "relief";

export const SEMANTIC_CATEGORY_COLOR: Record<SemanticFeedbackCategory, number> = {
  neutral: GameConfig.colors.biteChaos,
  positive: GameConfig.colors.star,
  negative: GameConfig.colors.danger,
  risk: GameConfig.colors.blackHoleRing,
  spatial: GameConfig.colors.wormholeCyan,
  relief: GameConfig.colors.speedTrialBlue,
};

/**
 * The single source of truth for which category each CHAOS-result (and
 * closely-related) headline belongs to - a future CHAOS result declares
 * its category here, once, rather than any call site inventing its own
 * color. Deliberately a lookup table, not a switch with inline colors -
 * see this module's own top-level doc. Every OTHER `HeadlineKind` (gate/
 * STARFRUIT/level-complete headlines - not CHAOS results) is intentionally
 * left unmapped, so it keeps rendering in the pre-existing neutral
 * headline white, exactly as before this change; see
 * `headlineSemanticColor()`.
 */
const HEADLINE_SEMANTIC_CATEGORY: Partial<Record<HeadlineKind, SemanticFeedbackCategory>> = {
  "chaos-anticipation": "neutral",
  jackpot: "positive",
  "wormhole-activate": "spatial",
  "wormhole-refresh": "spatial",
  "speed-trial-activate": "risk",
  // Not itself a CHAOS outcome - the reward for SURVIVING one - but
  // exactly as much a "positive/reward" result as JACKPOT, so it shares
  // that category rather than staying neutral.
  "speed-trial-success": "positive",
  shrink: "negative",
  // Legacy Daily V1-V4 outcome (see `mapEvents.ts`'s own doc) - still a
  // score PENALTY whenever it renders, so it shares SHRINK's negative
  // family rather than reading as neutral.
  "score-hit": "negative",
};

/**
 * The headline color for `kind`, or `null` for every `HeadlineKind` this
 * module deliberately leaves unmapped (every non-CHAOS-result headline -
 * gate/STARFRUIT/level-complete text) - the caller falls back to the
 * existing default headline color for `null`, so this function can never
 * accidentally recolor something nobody asked to have recolored.
 */
export function headlineSemanticColor(kind: HeadlineKind): number | null {
  const category = HEADLINE_SEMANTIC_CATEGORY[kind];
  return category ? SEMANTIC_CATEGORY_COLOR[category] : null;
}
